import React from 'react';
// @ts-ignore
import { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

interface NotificationProps {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
  onClose?: () => void;
  isVisible: boolean;
}

interface NotificationContainerProps {
  type: 'success' | 'error' | 'info' | 'warning';
}

// Industry standard durations: info/success = 3s, warning = 4s, error = 5s
const getDefaultDuration = (type: string): number => {
  switch (type) {
    case 'info': return 2500;
    case 'success': return 3000;
    case 'warning': return 4000;
    case 'error': return 5000;
    default: return 3000;
  }
};

const NotificationContainer = styled(motion.div)<NotificationContainerProps>`
  position: fixed;
  bottom: 16px;
  right: 16px;
  min-width: 140px;
  max-width: 260px;
  padding: 8px 12px;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 9999;
  backdrop-filter: blur(12px);
  overflow: hidden;
  
  background-color: ${props => {
    switch (props.type) {
      case 'success': return 'rgba(240, 253, 244, 0.95)';
      case 'error': return 'rgba(254, 242, 242, 0.95)';
      case 'warning': return 'rgba(255, 251, 235, 0.95)';
      case 'info': return 'rgba(255, 255, 255, 0.95)';
      default: return 'rgba(255, 255, 255, 0.95)';
    }
  }};
  
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success': return 'rgba(34, 197, 94, 0.2)';
      case 'error': return 'rgba(239, 68, 68, 0.2)';
      case 'warning': return 'rgba(245, 158, 11, 0.2)';
      case 'info': return 'rgba(0, 0, 0, 0.06)';
      default: return 'rgba(0, 0, 0, 0.06)';
    }
  }};
`;

const IconContainer = styled.div<{ type: 'success' | 'error' | 'info' | 'warning' }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => {
    switch (props.type) {
      case 'success': return '#22c55e';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#3b82f6';
    }
  }};
`;

const Message = styled.div`
  flex: 1;
  font-size: 12px;
  color: #1d1d1f;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #aeaeb2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: 4px;
  transition: all 0.15s ease;
  flex-shrink: 0;
  
  &:hover {
    color: #636366;
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const ProgressBar = styled.div<{ duration: number; type: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  border-radius: 0 0 0 10px;
  background-color: ${props => {
    switch (props.type) {
      case 'success': return '#22c55e';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#3b82f6';
    }
  }};
  animation: shrink ${props => props.duration}ms linear forwards;
  
  @keyframes shrink {
    from { width: 100%; }
    to { width: 0%; }
  }
`;

const getIcon = (type: 'success' | 'error' | 'info' | 'warning') => {
  switch (type) {
    case 'success':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      );
    case 'error':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      );
    case 'warning':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      );
    case 'info':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      );
    default:
      return null;
  }
};

const Notification: React.FC<NotificationProps> = ({
  type,
  message,
  duration,
  onClose,
  isVisible
}) => {
  // Use type-specific duration if not provided
  const effectiveDuration = duration || getDefaultDuration(type);
  
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, effectiveDuration);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, effectiveDuration, onClose]);

  return (
    <AnimatePresence>
      {isVisible && (
        <NotificationContainer
          type={type}
          initial={{ y: 20, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 10, opacity: 0, scale: 0.95 }}
          transition={{ 
            duration: 0.2, 
            ease: [0.16, 1, 0.3, 1]
          }}
        >
          <IconContainer type={type}>
            {getIcon(type)}
          </IconContainer>
          <Message>{message}</Message>
          <CloseButton onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </CloseButton>
          <ProgressBar duration={effectiveDuration} type={type} />
        </NotificationContainer>
      )}
    </AnimatePresence>
  );
};

export default Notification;
